import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ThemeProvider } from '@mui/material/styles';
import ResearchTheme from '../../styles/Theme.js';
import { api } from '../../services/api.js';
import { paymentApi } from '../../services/paymentApi.js';
import { surveyLimitService } from '../../services/surveyLimitService.js';
import { FREE_RESEARCH_LIMITS } from '../../payment/PricingUtils.js';
import { event } from '../../services/analyticsService.js';
import Question from '../../components/Question.js';

function SurveyEditor({ 
  userData, 
  setUserData, 
  onTopicsChange, 
  setTabIndex, 
  onDuplicateResearch, 
  isAdmin, 
  hasActiveSubscription, 
  walletBalance, 
  credits, 
  updateUserAttributes
}) {
  const [editedSurvey, setEditedSurvey] = useState(userData.surveyQuestions || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [openEditSnackbar, setOpenEditSnackbar] = useState(false);
  const [openWarningSnackbar, setOpenWarningSnackbar] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  // Add this effect to update editedSurvey when userData changes (like when switching topics)
  useEffect(() => {
    setEditedSurvey(userData.surveyQuestions || []);
  }, [userData.surveyQuestions]);

  useEffect(() => {
    if (userData.surveyStatus === 'EDITING') {
      setOpenEditSnackbar(true);
    } else {
      setOpenEditSnackbar(false);
    }
  }, [userData.surveyStatus]);

  const renderAddQuestionButton = () => {
    const maxQuestions = isAdmin 
      ? 20 // Admin limit
      : hasActiveSubscription 
        ? 15 // SUBSCRIPTION_LIMITS.MAX_QUESTIONS
        : 10; // FREE_RESEARCH_LIMITS.MAX_QUESTIONS

    if (editedSurvey.length >= maxQuestions) {
      return null; // Don't show the button if at or over the limit
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Button onClick={addQuestion}>
          Add Question
        </Button>
      </Box>
    );
  };

  const handleContinue = async () => {
    try {
      setLoading(true);
      setError(null);

      let validationFailed = false;

      // Validate questions
      for (let i = 0; i < editedSurvey.length; i++) {
        const question = editedSurvey[i];

        if (!question.questionText || !question.questionText.trim()) {
          setWarningMessage(`Question #${i + 1} has no text. Please enter some text for this question.`);
          validationFailed = true;
          setOpenWarningSnackbar(true);
          setLoading(false);
          return;
        }

        if (question.questionType === 'multiple-choice') {
          if (!question.responseOptions || question.responseOptions.some(opt => !opt.trim())) {
            setWarningMessage(`Some options for question #${i + 1} are empty. Please fill in all options.`);
            validationFailed = true;
            setOpenWarningSnackbar(true);
            setLoading(false);
            return;
          }

          if (question.responseOptions.length < 2) {
            setWarningMessage(`Question #${i + 1} must have at least 2 options.`);
            setOpenWarningSnackbar(true);
            setLoading(false);
            return;
          }

          if (question.hasFixedPercentages) {
            const sum = (question.responsePercentages || []).map(Number).reduce((a, b) => a + b, 0);
            if (sum !== 100) {
              setWarningMessage(`Fixed percentages for question #${i + 1} must sum to 100 (current sum: ${sum}).`);
              setOpenWarningSnackbar(true);
              validationFailed = true;
              setLoading(false);
              return;
            }
          }
        }
      }

      if (validationFailed) {
        return;
      }

      // Check question count limits based on subscription status
      const maxQuestions = isAdmin 
        ? 20 // Admin limit
        : hasActiveSubscription 
          ? 15 // SUBSCRIPTION_LIMITS.MAX_QUESTIONS
          : 10; // FREE_RESEARCH_LIMITS.MAX_QUESTIONS
      
      if (editedSurvey.length > maxQuestions) {
        const questionsToRemove = editedSurvey.length - maxQuestions;
        
        setWarningMessage(
          `You've exceeded the maximum questions limit. ` +
          `Please remove ${questionsToRemove} question${questionsToRemove > 1 ? 's' : ''} ` +
          `to continue${!hasActiveSubscription ? ' or upgrade to a subscription' : ''}.`
        );
        setOpenWarningSnackbar(true);
        setLoading(false);
        return;
      }

      console.log("📤 Sending questions to backend:", JSON.stringify(editedSurvey, null, 2));

      // Update the survey
      const result = await api.updateSurvey(
        userData.userID,
        userData.researchID,
        editedSurvey,
      );
      const survey = result.survey;
      let formattedQuestions = [];

      // Check the structure of survey.questions
      if (survey && survey.questions) {
        if (Array.isArray(survey.questions)) {
          // If it's already an array, use map directly
          formattedQuestions = survey.questions.map(q => ({
            questionID: q.id,
            questionText: q.text,
            questionType: q.type,
            responseOptions: q.options || [],
            maxLength: q.maxLength || 200,
            selectMin: q.selectMin || 1,
            selectMax: q.selectMax || 1,
            hasFixedPercentages: q.hasFixedPercentages || false,
            responsePercentages: q.responsePercentages || []
          }));
        } else if (typeof survey.questions === 'object') {
          // If it's an object mapping IDs to question data
          formattedQuestions = Object.entries(survey.questions).map(([id, q]) => ({
            questionID: id,
            questionText: q.prompt || q.text,
            questionType: q.kind || q.type,
            responseOptions: q.vocab || q.options || [],
            maxLength: q.max_length || q.maxLength || 200,
            selectMin: q.min || q.selectMin || 1,
            selectMax: q.max || q.selectMax || 1,
            hasFixedPercentages: q.hasFixedPercentages || false,
            responsePercentages: q.responsePercentages || []
          }));
        } else {
          // console.error("Unexpected survey.questions format:", survey.questions);
          setError('Survey format error: Please try again.');
          setLoading(false);
          return;
        }
      } else {
        // console.error("Missing survey or questions in API response:", result);
        setError('Survey data missing: Please try again.');
        setLoading(false);
        return;
      }

      setEditedSurvey(formattedQuestions);
      
      // Check if this is the first time advancing to personas (no personas yet)
      const isFirstTimeToPersonas = !userData.selectedPersonas || 
                                   Object.keys(userData.selectedPersonas).length === 0;

      // If this is the first time and the user is not an admin, check if they have at least 1 credit
      if (isFirstTimeToPersonas && !isAdmin) {
        try {
          // Only check if the user has at least one credit without deducting it
          const userDetails = await paymentApi.getUserDetails();
          const userCredits = parseInt(userDetails.credits || '0');
          
          // If no credits and no subscription, show error
          if (!hasActiveSubscription && userCredits <= 0 && walletBalance <= 0) {
            setWarningMessage(
              "You need credits, a subscription, or wallet funds to proceed to the personas step. " +
              "Please upgrade your account to continue."
            );
            setOpenWarningSnackbar(true);
            setLoading(false);
            return;
          }
        } catch (error) {
          // console.error("Error checking credits:", error);
        }
      }

      // Track successful survey completion
      event({
        action: 'research_step_completed',
        category: 'tool_usage',
        label: 'step_1_survey_creation',
        value: editedSurvey.length, // Number of questions as value
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);

      setUserData(prev => ({
        ...prev,
        userID: userData.userID,
        researchID: userData.researchID,
        surveyQuestions: formattedQuestions,
        surveyStatus: 'COMPLETED',
        lastModified: result.lastModified
      }));

      setTabIndex(1);
      
      if (onTopicsChange) {
        onTopicsChange();
      }

    } catch (err) {
      // Track error
      event({
        action: 'research_step_error',
        category: 'error',
        label: `survey_completion_error: ${err.message}`,
      });
      console.log("Error in updating survey", err);
      setError('Failed to update survey: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const addQuestion = () => {
    setEditedSurvey([...editedSurvey,   { 
      questionText: '', 
      questionType: 'open-ended', 
      responseOptions: [], 
      maxLength: 200,
      hasFixedPercentages: false,
      responsePercentages: [],
      selectMax: '',
      selectMin: ''
    }]);

    // Track question addition
    event({
      action: 'add_question',
      category: 'tool_usage',
      label: 'survey_editor',
      value: editedSurvey.length + 1, // New question count
    });
  };

  const handleQuestionChange = (index, field, value) => {
    const updatedQuestions = [...editedSurvey];
    updatedQuestions[index][field] = value;

    const question = updatedQuestions[index];

    const newValue = parseInt(value, 10);

    if (field === 'selectMax' || field === 'selectMin' || field === 'maxLength') {
      if (field === 'selectMax' && newValue < question.selectMin) {
        setWarningMessage(`Max cannot be less than Min. Adjusting max to ${question.selectMin}.`);
        question.selectMax = question.selectMin;
        setOpenWarningSnackbar(true);
      } else if (field === 'selectMin') {
        const numOptions = question.responseOptions ? question.responseOptions.length : 0;
        if (newValue > question.selectMax) {
          setWarningMessage(`Min cannot exceed Max. Adjusting min to ${question.selectMax}.`);
          question.selectMin = question.selectMax;
          setOpenWarningSnackbar(true);
        } else if (newValue > numOptions && question.questionType === 'multiple-choice') {
          setWarningMessage(`Min cannot exceed the number of options (${numOptions}). Adjusting min to ${numOptions}.`);
          question.selectMin = numOptions;
          setOpenWarningSnackbar(true);
        } else {
          question.selectMin = newValue;
        }
      } else if (field === 'maxLength' && newValue < 10) {
        setWarningMessage(`Max Length cannot be less than 10. Adjusting to 10.`);
        question.maxLength = 10;
        setOpenWarningSnackbar(true);
      } else {
        question[field] = newValue;
      }
    } else {
      question[field] = value;
    }

    let isValid = true;

    if ((field === 'questionText' && (!value || !value.trim())) || 
        (field === 'responseOptions' && question.responseOptions.some(opt => !opt.trim()))) {
      if (field === 'questionText') {
        setWarningMessage(`Question #${index + 1} has no text. Please enter some text for this question.`);
        isValid = false;

      } else if (field === 'responseOptions') {
        setWarningMessage(`Some options for question #${index + 1} are empty. Please fill in all options.`);
        isValid = false;
      }
    }

    if (!isValid) {
      setOpenWarningSnackbar(true);
      setUserData(prev => ({
        ...prev,
        surveyStatus: 'EDITING'
      }));
      setOpenEditSnackbar(true);
    } else {
      setEditedSurvey(updatedQuestions);
    }

    const shouldEdit = 
      !question.questionText?.trim() ||
      (!question.maxLength && question.questionType === 'open-ended') ||
      (question.questionType === 'multiple-choice' && (!question.responseOptions || question.responseOptions.length < 2)) ||
      (!question.selectMax && question.questionType === 'multiple-choice') ||
      (!question.selectMin && question.questionType === 'multiple-choice') ||
      (!question.selectMax && question.questionType === 'open-list') ||
      (!question.selectMin && question.questionType === 'open-list')
      ;

    if (shouldEdit) {
      setUserData(prev => ({
        ...prev,
        surveyStatus: 'EDITING'
      }));
      setOpenEditSnackbar(true);
    }
  };

  const removeQuestion = async (index) => {
    if (editedSurvey.length <= 1) {
      setWarningMessage('Cannot remove the last question. The survey must have at least one question.');
      setOpenWarningSnackbar(true);
      return; 
    }

    console.log("Length of editedSurvey before removal:", editedSurvey.length);

    const updatedSurvey = [...editedSurvey];
    updatedSurvey.splice(index, 1);
    setEditedSurvey(updatedSurvey);

    console.log("Length of editedSurvey after removal:", updatedSurvey.length);
  };

  const moveQuestionUp = async (index) => {
    if (index === 0) return;
    
    const updatedSurvey = [...editedSurvey];
    const temp = updatedSurvey[index];
    updatedSurvey[index] = updatedSurvey[index - 1];
    updatedSurvey[index - 1] = temp;
    
    setEditedSurvey(updatedSurvey);
  };

  const moveQuestionDown = async (index) => {
    if (index === editedSurvey.length - 1) return;
    
    const updatedSurvey = [...editedSurvey];
    const temp = updatedSurvey[index];
    updatedSurvey[index] = updatedSurvey[index + 1];
    updatedSurvey[index + 1] = temp;
    
    setEditedSurvey(updatedSurvey);
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updatedSurvey = [...editedSurvey];
    const question = updatedSurvey[questionIndex];

    question.responseOptions.splice(optionIndex, 1);

    const numOptions = question.responseOptions.length;

    if (question.selectMin > numOptions) {
      setWarningMessage(`Min cannot exceed the number of options (${numOptions}). Adjusting min to ${numOptions}.`);
      question.selectMin = numOptions;
      setOpenWarningSnackbar(true);
    }

    if (question.questionType === 'multiple-choice' && question.responseOptions.length < 2) {
      setUserData(prev => ({
        ...prev,
        surveyStatus: 'EDITING'
      }));

      setOpenEditSnackbar(true);

      setWarningMessage(
        `Question ${questionIndex + 1} now has only ${question.responseOptions.length} option(s). You must have at least 2 before continuing.`)

      setOpenWarningSnackbar(true);
    }

    setEditedSurvey(updatedSurvey);
  };

  const addOption = (questionIndex) => {
    const updatedSurvey = [...editedSurvey];
    updatedSurvey[questionIndex].responseOptions.push('');
    setEditedSurvey(updatedSurvey);

    setUserData(prev => ({
        ...prev,
        surveyStatus: 'EDITING'
      }));
  };

  const toggleQuestionType = (questionIndex, newType) => {
    const updatedSurvey = [...editedSurvey];
    updatedSurvey[questionIndex].questionType = newType;
    setEditedSurvey(updatedSurvey);
  };

  return (
    <ThemeProvider theme={ResearchTheme}>
      <Box>
        {showSuccess && (
          <Alert severity="success" sx={{ mb: 2, bgcolor: 'primary.main', color: 'white' }}>
            Successfully saved survey
          </Alert>
        )}

        {editedSurvey && editedSurvey.length > 0 && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 4 }}>
              Edit Survey
            </Typography>

            {editedSurvey.map((question, index) => (
              <Question
                key={index}
                question={question}
                index={index}
                handleQuestionChange={handleQuestionChange}
                removeQuestion={removeQuestion}
                addOption={addOption}
                removeOption={removeOption}
                toggleQuestionType={toggleQuestionType}
                moveQuestionUp={moveQuestionUp}
                moveQuestionDown={moveQuestionDown}
                totalQuestions={editedSurvey.length}
              />
            ))}

            {renderAddQuestionButton()}

            <Button
              variant="contained"
              onClick={handleContinue}
              disabled={loading}
              sx={{ mt: 2, display: 'block', mx: 'auto' }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Saving...
                </Box>
              ) : (
                'Save and Continue'
              )}
            </Button>

            {userData.researchID && (
              <Button
                variant="outlined"
                onClick={onDuplicateResearch}
                sx={{ 
                  mt: 2, 
                  display: 'block', 
                  mx: 'auto',
                  color: 'var(--tertiary-color)',
                  borderColor: 'var(--tertiary-color)',
                  '&:hover': {
                    borderColor: 'var(--secondary-color)',
                    backgroundColor: 'rgba(var(--tertiary-color-rgb), 0.1)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ContentCopyIcon sx={{ mr: 1 }} />
                  Duplicate This Research
                </Box>
              </Button>
            )}
          </Paper>
        )}

        <Snackbar
          open={openWarningSnackbar}
          autoHideDuration={6000}
          onClose={() => setOpenWarningSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setOpenWarningSnackbar(false)} severity="warning">
            {warningMessage}
          </Alert>
        </Snackbar>

        <Snackbar
          open={openEditSnackbar}
          onClose={() => setOpenEditSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          autoHideDuration={null}
          sx={{ mb: 8 }}
        >
          <Alert onClose={() => setOpenEditSnackbar(false)} severity="warning">
            Please save your changes before moving to the next tab.
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}

export default SurveyEditor;